// Blog data loader - fetches from CDN or falls back to local data
// This allows blog data to be updated without redeploying the app

import { blogPosts as localBlogPosts } from './blogData';
import { CDN_URLS } from '../config/cdnConfig';

// CDN URL for blog data (can be overridden via environment variable)
// Use backend proxy to avoid CORS issues, fallback to CDN or direct GCS URL
const getBlogDataUrl = () => {
  if (process.env.REACT_APP_BLOG_DATA_URL) {
    return process.env.REACT_APP_BLOG_DATA_URL;
  }
  // Try backend proxy first (no CORS issues)
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.snageasy.in';
  if (apiUrl) {
    return `${apiUrl}/api/blog-data`;
  }
  // Fallback to CDN URL (or direct GCS if CDN not configured)
  return CDN_URLS.BLOG_DATA;
};

const BLOG_DATA_CDN_URL = getBlogDataUrl();

// Cache for fetched blog data
let cachedBlogPosts = null;
let fetchPromise = null;

/**
 * Fetch blog posts from CDN
 * @returns {Promise<Array>} Array of blog posts
 */
const fetchBlogPostsFromCDN = async () => {
  try {
    console.log(`[Blog Data] Fetching from CDN: ${BLOG_DATA_CDN_URL}`);
    const response = await fetch(BLOG_DATA_CDN_URL, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: 'default',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (Array.isArray(data) && data.length > 0) {
      console.log(`[Blog Data] Successfully loaded ${data.length} posts from CDN`);
      return data;
    }
    throw new Error('Invalid data format from CDN');
  } catch (error) {
    console.warn(`[Blog Data] Failed to fetch from CDN: ${error.message}`);
    if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
      console.warn(`[Blog Data] CORS error detected. Check CORS configuration on GCS bucket.`);
      console.warn(`[Blog Data] Run: ./scripts/set-gcs-cors.sh`);
    }
    console.warn(`[Blog Data] Falling back to local blog data`);
    return null;
  }
};

/**
 * Get blog posts (from cache, CDN, or local fallback)
 * @returns {Promise<Array>} Array of blog posts
 */
const getBlogPosts = async () => {
  // Return cached data if available
  if (cachedBlogPosts) {
    return cachedBlogPosts;
  }

  // If fetch is in progress, wait for it
  if (fetchPromise) {
    return fetchPromise;
  }

  // Fetch from CDN
  fetchPromise = fetchBlogPostsFromCDN().then(data => {
    if (data && Array.isArray(data) && data.length > 0) {
      cachedBlogPosts = data;
      return data;
    }
    // Fallback to local data
    cachedBlogPosts = localBlogPosts;
    return localBlogPosts;
  }).catch(() => {
    // On error, use local fallback
    cachedBlogPosts = localBlogPosts;
    return localBlogPosts;
  });

  return fetchPromise;
};

/**
 * Get all blog posts (async - fetches from CDN if available)
 */
export const getAllBlogPosts = async () => {
  return await getBlogPosts();
};

/**
 * Get blog post by slug (async)
 */
export const getBlogPostBySlug = async (slug) => {
  const posts = await getBlogPosts();
  return posts.find(post => post.slug === slug);
};

/**
 * Get related posts (async)
 */
export const getRelatedPosts = async (currentSlug, limit = 3) => {
  const posts = await getBlogPosts();
  return posts
    .filter(post => post.slug !== currentSlug)
    .slice(0, limit);
};

// Initialize: Try to fetch from CDN on module load (non-blocking)
if (typeof window !== 'undefined') {
  getBlogPosts().catch(err => {
    console.error('[Blog Data] Error initializing blog data:', err);
  });
}
